import React, {
  useState,
  useEffect,
  useContext
} from "react";

import { Rnd } from "react-rnd";

import { crudeDatabase } from "../data/crudeDatabase";

import { WellContext } from "../context/WellContext";

import "./MDSFaceplate.css";

/* =========================
   STRICT VALUE PARSER
========================= */
function parseValue(val) {

  if (val === null || val === undefined) {
    return { value: "", unit: "" };
  }

  if (typeof val === "object") {

    if (val.value !== undefined || val.unit !== undefined) {

      return {
        value: String(val.value ?? ""),
        unit: String(val.unit ?? "")
      };
    }

    return {
      value: "",
      unit: ""
    };
  }

  const str = String(val);

  const match =
    str.match(/^([0-9.\-\s<>]+)\s*(.*)$/);

  return {
    value: match ? match[1].trim() : str,
    unit: match ? match[2].trim() : ""
  };
}

/* =========================
   EDITABLE BLOCK
========================= */
function EditableBlock({
  title,
  data,
  isEditing,
  setEditableCrude,
  path
}) {

  if (!data) return null;

  /* =========================
     SIMPLE VALUE EDIT
  ========================= */

  const handleSimpleChange = (
    key,
    newValue
  ) => {

    setEditableCrude((prev) => ({
      ...prev,

      [path]: {
        ...prev[path],

        [key]: {
          ...prev[path][key],
          value: newValue
        }
      }
    }));

  };

  /* =========================
     NESTED VALUE EDIT
  ========================= */

  const handleNestedChange = (
    key,
    subKey,
    newValue
  ) => {

    setEditableCrude((prev) => ({
      ...prev,

      [path]: {
        ...prev[path],

        [key]: {
          ...prev[path][key],

          [subKey]: {
            ...prev[path][key][subKey],
            value: newValue
          }
        }
      }
    }));

  };

  return (

    <div className="mds-section">

      <h3 className="mds-section-title">
        {title}
      </h3>

      <div className="mds-grid">

        {Object.entries(data).map(([key, val]) => {

          const isNested =
            typeof val === "object" &&
            !("value" in val) &&
            !("unit" in val);

          /* =========================
             NESTED BLOCKS
          ========================= */

          if (isNested) {

            return (

              <div
                key={key}
                className="mds-nested-block"
              >

                <div className="mds-nested-title">
                  {key}
                </div>

                {Object.entries(val).map(
                  ([subKey, subVal]) => {

                    const parsed =
                      parseValue(subVal);

                    return (

                      <div
                        key={subKey}
                        className="mds-row"
                      >

                        <div className="mds-key">
                          {subKey}
                        </div>

                        <div className="mds-value-wrapper">

                          <div className="mds-value-box">

                            {isEditing ? (

                              <input
                                className="mds-input"

                                value={parsed.value}

                                onChange={(e) =>
                                  handleNestedChange(
                                    key,
                                    subKey,
                                    e.target.value
                                  )
                                }
                              />

                            ) : (

                              <span className="mds-value-text">
                                {parsed.value}
                              </span>

                            )}

                          </div>

                          <div className="mds-unit-box">
                            {parsed.unit || "-"}
                          </div>

                        </div>

                      </div>

                    );
                  }
                )}

              </div>
            );
          }

          /* =========================
             NORMAL BLOCKS
          ========================= */

          const parsed = parseValue(val);

          return (

            <div
              key={key}
              className="mds-row"
            >

              <div className="mds-key">
                {val?.name || key}
              </div>

              <div className="mds-value-wrapper">

                <div className="mds-value-box">

                  {isEditing ? (

                    <input
                      className="mds-input"

                      value={parsed.value}

                      onChange={(e) =>
                        handleSimpleChange(
                          key,
                          e.target.value
                        )
                      }
                    />

                  ) : (

                    <span className="mds-value-text">
                      {parsed.value}
                    </span>

                  )}

                </div>

                <div className="mds-unit-box">
                  {parsed.unit || "-"}
                </div>

              </div>

            </div>

          );
        })}

      </div>

    </div>

  );
}

/* =========================
   MAIN COMPONENT
========================= */

function Analyzer1Faceplate({

  close = () => {},

  defaultPosition = {
    x: 120,
    y: 80
  }

}) {

  const { transmitters } =
    useContext(WellContext);

  const [selectedCrude, setSelectedCrude] = useState(() => {
    return localStorage.getItem("Analyzer1_selectedCrude") || "";
  });

  useEffect(() => {
    localStorage.setItem(
      "Analyzer1_selectedCrude",
      selectedCrude
    );
  }, [selectedCrude]);

  const [activeTab, setActiveTab] =
    useState("crudeName");

  const [isEditing, setIsEditing] =
    useState(false);

  const [editableCrude, setEditableCrude] =
    useState(null);

  const [faceplatePos, setFaceplatePos] = useState(() => {

    const saved =
      localStorage.getItem(
        "Analyzer1_faceplatePos"
      );

    return saved
      ? JSON.parse(saved)
      : { x: 120, y: 80 };

  });

  useEffect(() => {

    localStorage.setItem(
      "Analyzer1_faceplatePos",
      JSON.stringify(faceplatePos)
    );

  }, [faceplatePos]);

  const crude =
    editableCrude ||
    crudeDatabase[selectedCrude];

  /* =========================================
     SECTION RENDERER
  ========================================= */

  const renderSection = () => {

    if (!crude) {

      return (
        <div className="mds-empty">
          Select Crude Type
        </div>
      );
    }

    switch (activeTab) {

      /* =====================================
         CRUDE NAME
      ===================================== */

      case "crudeName":

        return (
          <div className="mds-section">

            <h3>
              CRUDE NAME
            </h3>

            <div className="mds-info-card">

              <div className="mds-info-row">

                <span>
                  Name
                </span>

                <span>
                  {crude.name}
                </span>

              </div>

              <div className="mds-info-row">

                <span>
                  Country
                </span>

                <span>
                  {crude.country}
                </span>

              </div>

              <div className="mds-info-row">

                <span>
                  Type
                </span>

                <span>
                  {crude.type}
                </span>

              </div>

            </div>

          </div>
        );

      /* =====================================
         OTHER TABS
      ===================================== */

      default:

        return (
          <EditableBlock
            title={activeTab.toUpperCase()}

            data={crude[activeTab]}

            isEditing={isEditing}

            setEditableCrude={
              setEditableCrude
            }

            path={activeTab}
          />
        );
    }
  };

  return (

    <Rnd
      position={faceplatePos}

      onDragStop={(e, d) => {
        setFaceplatePos({
          x: d.x,
          y: d.y
        });
      }}

      minWidth={750}
      minHeight={520}

      dragHandleClassName="mds-header"

      className="mds-rnd"
    >

      <div className="mds-faceplate">

        {/* HEADER */}

        <div className="mds-header">

          <div className="mds-title">
            CRUDE SAMPLE ANALYZER
          </div>

          <button
            className="mds-close-btn"
            onClick={close}
          >
            ✕
          </button>

        </div>

        {/* CONTROLS */}

        <div className="mds-controls">

          <select
            value={selectedCrude}

            onChange={(e) =>
              setSelectedCrude(
                e.target.value
              )
            }

            className="mds-select"
          >

            <option value="">
              Select Crude
            </option>

            {Object.entries(
              crudeDatabase
            ).map(([k, v]) => (

              <option
                key={k}
                value={k}
              >
                {v.name}
              </option>

            ))}

          </select>

          <button
            className={`mds-edit-btn ${
              isEditing ? "active" : ""
            }`}

            onClick={() =>
              setIsEditing(!isEditing)
            }
          >

            {isEditing
              ? "LOCK"
              : "EDIT"}

          </button>

        </div>

        {/* BODY */}

        <div className="mds-body">

          {/* LEFT PANEL */}

          <div className="mds-left-panel">

            {[
              ["crudeName", "Crude Name"],
              ["physicalProperties", "Physical"],
              ["corrosionStability", "Corrosion"],
              ["tbpDistillation", "Distillation"]
            ].map(([k, label]) => (

              <div
                key={k}

                className={`mds-tab ${
                  activeTab === k
                    ? "active"
                    : ""
                }`}

                onClick={() =>
                  setActiveTab(k)
                }
              >

                {label}

              </div>

            ))}

          </div>

          {/* RIGHT PANEL */}

          <div className="mds-right-panel"> 
            {renderSection()}
          </div>

        </div>

        {/* DISCLAIMER */}

        <div className="mds-disclaimer">

          <strong>DISCLAIMER:</strong>

          The crude oil properties and analyzer data
          displayed within this Crude Sample Analyzer
          interface are intended solely for refinery
          process monitoring, operator training,
          digital twin simulation, and DCS visualization
          purposes.

        </div>

        {/* FOOTER */}

        <div className="mds-footer">

          <div className="mds-footer-title">
            NK Refinery DCS and Digital Twin System
          </div>

          <div className="mds-footer-actions">

            <button className="mds-btn">
              Print
            </button>

            <button className="mds-btn">
              Share
            </button>

            <button className="mds-btn">
              Export
            </button>

          </div>

        </div>

      </div>

    </Rnd>

  );
}

export default Analyzer1Faceplate;
